import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { LoginForm } from './LoginForm';
import { LoginBackgroundSlideshow } from './LoginBackgroundSlideshow';
import { ServiceUnavailablePage } from './ServiceUnavailablePage';
import { BrandLogo } from '@components/common/BrandLogo';
import { useTenantInfo } from '../hooks/useTenantInfo';
import { useAuthStore } from '../store/authStore';
import { apiClient } from '@lib/api/client';

interface AzureNavigationState {
  azureEmail?: string;
  azureRef?: string;
}

/** Marcas de registro en las esquinas de la tarjeta. */
function CornerMarks() {
  const base = 'pointer-events-none absolute h-3.5 w-3.5 border-secondary-300';
  return (
    <>
      <span aria-hidden="true" className={`${base} left-[18px] top-[18px] rounded-tl-[3px] border-l-[1.5px] border-t-[1.5px]`} />
      <span aria-hidden="true" className={`${base} right-[18px] top-[18px] rounded-tr-[3px] border-r-[1.5px] border-t-[1.5px]`} />
      <span aria-hidden="true" className={`${base} bottom-[18px] left-[18px] rounded-bl-[3px] border-b-[1.5px] border-l-[1.5px]`} />
      <span aria-hidden="true" className={`${base} bottom-[18px] right-[18px] rounded-br-[3px] border-b-[1.5px] border-r-[1.5px]`} />
    </>
  );
}

export function LoginPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showExpired, setShowExpired] = useState(false);
  const [showAzureError, setShowAzureError] = useState(false);
  const { tenantInfo, isLoading: isTenantLoading, isServiceUnavailable } = useTenantInfo();
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();
  const location = useLocation();

  // Verificación con Microsoft ya completada (ver AzureCallbackPage.tsx): el
  // email viene verificado y bloqueado, solo falta el password de Aludra.
  // Se lee una sola vez al montar — el state de navegación no sobrevive un
  // refresh de página, lo cual es correcto (el azureRef es de un solo uso).
  const [azureVerification] = useState<{ email: string; azureRef: string } | null>(() => {
    const state = location.state as AzureNavigationState | null;
    if (state?.azureEmail && state?.azureRef) {
      return { email: state.azureEmail, azureRef: state.azureRef };
    }
    return null;
  });

  useEffect(() => {
    if (searchParams.get('expired') === '1') {
      setShowExpired(true);
      searchParams.delete('expired');
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get('azureError') === '1') {
      setShowAzureError(true);
      searchParams.delete('azureError');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-login silencioso con RememberMe al cargar la página.
  // También corre cuando el usuario viene de verificar con Microsoft
  // (azureVerification): el login asistido por Azure fuerza rememberMe=true
  // en el backend (ver auth.controller.ts), así que en visitas subsecuentes
  // ya debe haber cookies rm_email/rm_session — sin este chequeo, siempre se
  // volvería a pedir el password aunque ya esté "recordado".
  useEffect(() => {
    if (showExpired) return; // no auto-login si la sesión expiró explícitamente
    apiClient.get('/auth/check-remember-me').then((res) => {
      const data = res.data as any;
      if (data?.hasRememberMe && data?.accessToken) {
        // Seguridad: si venimos de verificar con Microsoft, la cookie
        // RememberMe SOLO es válida para auto-login si pertenece al MISMO
        // email que Microsoft verificó. En una computadora compartida la
        // cookie puede ser de otro usuario — en ese caso no se debe adoptar
        // esa sesión, simplemente se ignora la respuesta y se deja el
        // formulario pidiendo el password del email verificado.
        if (
          azureVerification &&
          data.user?.email?.toLowerCase() !== azureVerification.email.toLowerCase()
        ) {
          return;
        }
        setAuth(data.user, data.accessToken, data.sessionId);
        navigate('/home/main', { replace: true });
      }
    }).catch(() => {
      // Sin cookies RememberMe o fallo silencioso — mostrar formulario
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isTenantLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-950">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-teal-500" />
      </div>
    );
  }

  if (isServiceUnavailable) {
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" data-testid="auth-login-page-tenant-loading-spinner" />
    return <ServiceUnavailablePage />;
  }


  // La tarjeta del formulario topea en 520px, así que darle una fracción del
  // ancho (1fr) le regalaba ~200px muertos. Acotando esa columna a 620px, el
  // resto queda para el hero fotográfico.
  return (
    <div className="grid min-h-screen w-full grid-cols-1 bg-white font-display lg:grid-cols-[minmax(0,1fr)_minmax(0,620px)]">
      {/* ===================== IZQUIERDA — HERO ===================== */}
      {/*
        En móvil/tablet el hero va DEBAJO del formulario: apilado arriba
        ocupaba ~500px de 780 y dejaba el login fuera del pliegue. Desde lg
        vuelve a la izquierda, como en el diseño.
      */}
      <aside
        className="relative order-2 flex flex-col justify-between gap-8 overflow-hidden bg-ink-950 px-6 py-10 text-white sm:px-10 lg:order-1 lg:gap-0 lg:px-[clamp(32px,4.5vw,64px)] lg:py-[clamp(32px,4vw,56px)]"
      >
        {/* Fondo fotográfico dinámico (fotos de Panamá con crossfade + Ken
            Burns). Va primero para quedar debajo del grano y todo el
            contenido. La "línea arquitectónica" que iba acá (divisor vertical
            a 64%) se quitó: estaba pensada para separar dos superficies
            planas y sobre una foto se leía como una raya suelta cruzando
            la imagen. */}
        <LoginBackgroundSlideshow />

        {/* Grano de papel */}
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full opacity-[.18] mix-blend-overlay"
        >
          <filter id="paper-grain">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
            <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.35 0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#paper-grain)" />
        </svg>

        {/*
          Misma marca que el portal por dentro (BrandLogo): antes esta pantalla
          mostraba el logo del tenant cuando existia y caia a una marca propia
          cuando no, asi que el logo cambiaba entre el login y el interior.
        */}
        <div className="relative z-[2] flex items-center gap-3.5 lg:ml-4 lg:mt-5">
          <BrandLogo markSize={72} wordmarkClassName="text-[22px]" imgTestId="auth-login-page-tenant-logo-image" />
        </div>

        {/* Copy */}
        <div className="relative z-[2] max-w-[560px]">
          <div className="mb-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-white/50 sm:mb-7 sm:text-[12px]">
            Facturación Electrónica · Panamá
          </div>
          <h1 className="m-0 text-[clamp(30px,2.7vw,48px)] font-bold leading-[1.05] tracking-[-0.035em] text-white">
            Tu facturación
            <br />
            electrónica,
            <br />
            <span className="font-medium text-white/[.78]">en regla con la DGI.</span>
          </h1>
          <p className="mt-6 max-w-[400px] text-base leading-[1.6] text-white/[.55]">
            Emite, consulta y archiva tus documentos fiscales sin salir del flujo de trabajo.
          </p>
        </div>

        {/* Pie — en móvil el copyright completo es demasiado largo para una
            línea junto a la versión, así que se apila. */}
        <div className="relative z-[2] flex flex-col gap-1 text-[12px] text-white/[.35] sm:flex-row sm:items-center sm:justify-between sm:text-xs">
          <span>
            © {new Date().getFullYear()} Facturación Electrónica. Todos los derechos reservados.
          </span>
          <span className="font-mono tracking-[0.08em]">v{__APP_VERSION__}</span>
        </div>
      </aside>

      {/* ===================== DERECHA — LOGIN ===================== */}
      <main className="order-1 flex items-center justify-center overflow-y-auto bg-secondary-100 p-4 sm:p-6 lg:order-2 lg:p-10 short:lg:p-6">
        <div className="relative w-full max-w-[520px] rounded-3xl border border-[#f4f4f4] bg-white px-6 pb-11 pt-12 shadow-[0_1px_1px_rgba(10,34,51,.02),0_30px_60px_-30px_rgba(10,34,51,.08)] sm:px-14 sm:pt-[60px] short:pb-7 short:pt-8 short:sm:pt-9">
          <CornerMarks />

          <div className="mb-5 text-[12px] font-semibold uppercase tracking-[0.28em] text-secondary-400 short:mb-2">
            Acceso
          </div>

          <h2 className="m-0 text-4xl font-bold leading-[1.1] tracking-[-0.03em] text-ink-850 short:text-3xl">
            Inicia sesión
          </h2>
          <p className="mb-10 mt-3 text-[15px] leading-[1.55] text-secondary-500 short:mb-5 short:mt-1.5">
            Ingresa a tu espacio de facturación.
          </p>

          {showAzureError && (
            <div
              className="mb-6 rounded-xl border border-danger-500/20 bg-danger-50 p-4"
              data-testid="auth-login-page-azure-error-alert"
            >
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-danger-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-danger-700">
                    No se pudo iniciar sesión con Microsoft. Intenta nuevamente o usa tu correo y contraseña.
                  </p>
                </div>
              </div>
            </div>
          )}

          {showExpired && (
            <div
              className="mb-6 rounded-xl border border-warning-500/25 bg-warning-50 p-4"
              data-testid="auth-login-page-session-expired-alert"
            >
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-warning-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-warning-600">
                    Tu sesión ha expirado. Por favor, inicia sesión nuevamente.
                  </p>
                </div>
              </div>
            </div>
          )}

          <LoginForm azureAdEnabled={tenantInfo?.azureAdEnabled} azureVerification={azureVerification} />
        </div>
      </main>
    </div>
  );
}
